import * as RadixDialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../lib/media.ts';
import { IconButton } from '../actions/IconButton.tsx';
import styles from './Dialog.module.css';
import { Sheet } from './Sheet.tsx';

export type DialogSize = 'sm' | 'md' | 'lg';

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"]';

/**
 * Focus goes to the first field of the form (where typing starts), not to the close button, whose
 * tooltip would otherwise pop up on every opening. Without fields, the dialog itself takes focus.
 */
const focusFirstField = (event: Event) => {
  event.preventDefault();
  const content = event.currentTarget as HTMLElement;
  const field = content.querySelector<HTMLElement>(FIELD_SELECTOR);
  (field ?? content).focus();
};

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  readonly size?: DialogSize | undefined;
  /** Actions; the primary one goes last (it lands on the right on desktop, on top on phones). */
  readonly footer?: ReactNode | undefined;
  readonly children: ReactNode;
}

/** Modal task. A centred dialog on tablets and desktops, a bottom sheet on phones. */
export const Dialog = ({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  footer,
  children,
}: DialogProps) => {
  const { t } = useTranslation();
  const phone = useMediaQuery('phone');
  if (phone) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange} title={title} description={description} footer={footer}>
        {children}
      </Sheet>
    );
  }
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        <RadixDialog.Content
          className={clsx(styles.content, styles[size])}
          onOpenAutoFocus={focusFirstField}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <header className={styles.header}>
            <div className={styles.heading}>
              <RadixDialog.Title className={styles.title}>{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className={styles.description}>
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon={X} label={t('ui.close')} size="sm" />
            </RadixDialog.Close>
          </header>
          <div className={styles.body}>{children}</div>
          {footer && <footer className={styles.footer}>{footer}</footer>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};
